import { Link } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import { AgentLogoImage } from "@/components/agents/AgentLogoImage";
import {
	coerceAgentCategories,
	getAgentCategoryLabel,
} from "@/lib/agent-categories";
import { currencyAtom } from "@/lib/currency";
import { formatCompactCurrency as formatCompactCurrencyUtil } from "@/lib/format";
import type { PublicAgentView } from "@/lib/public-agents";

function AgentStat({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex flex-col gap-0.5">
			<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
				{label}
			</span>
			<span className="whitespace-nowrap font-mono text-base font-normal tabular-nums text-foreground">
				{value}
			</span>
		</div>
	);
}

export function AgentCard({ agent }: { agent: PublicAgentView }) {
	const currency = useAtomValue(currencyAtom);

	return (
		<Link
			to="/agents/$agentSlug"
			params={{ agentSlug: agent.slug }}
			className="group flex flex-col border border-border bg-card p-4 transition-colors hover:border-brand/40 hover:bg-brand-soft active:bg-brand-soft/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background"
		>
			<div className="flex items-center gap-3">
				<AgentLogoImage
					logoUrl={agent.logoUrl}
					website={agent.displayWebsite}
					mode={agent.displayAvatarMode}
					name={agent.displayInitials}
					size="sm"
				/>
				<div className="min-w-0 flex-1">
					<p className="truncate text-base font-normal text-foreground transition-colors group-hover:text-brand">
						{agent.displayName}
					</p>
					<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
						{coerceAgentCategories(agent.categories)
							.map((category) => getAgentCategoryLabel(category))
							.join(" · ")}
					</span>
				</div>
			</div>

			<p className="mt-3 line-clamp-2 text-pretty text-sm leading-[1.55] text-muted-foreground">
				{agent.displayDescription}
			</p>

			<div className="mt-auto pt-4">
				<div className="grid grid-cols-3 gap-3 border-t border-border pt-3">
					<AgentStat
						label="MRR"
						value={formatCompactCurrencyUtil(agent.estimatedMrr, currency)}
					/>
					<AgentStat
						label="Subs"
						value={String(agent.currentActiveSubscriptions ?? 0)}
					/>
					<AgentStat
						label="Total"
						value={formatCompactCurrencyUtil(agent.allTimeRevenue, currency)}
					/>
				</div>
			</div>
		</Link>
	);
}
